import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Monogram } from "./Ornaments";
import { scrollToId } from "./data";

const LINKS = [
    { id: "invitation", label: "Invitation", testid: "nav-invitation-link" },
    { id: "gallery", label: "Gallery", testid: "nav-gallery-link" },
    { id: "celebrations", label: "Celebrations", testid: "nav-events-link" },
    { id: "venues", label: "Directions", testid: "nav-venues-link" },
    { id: "rsvp", label: "RSVP", testid: "nav-rsvp-link" },
];

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                scrolled
                    ? "border-b border-gold/15 bg-obsidian/80 backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <nav className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-5 sm:px-8">
                <button
                    data-testid="nav-hero-link"
                    onClick={() => go("hero")}
                    className="group flex items-center gap-3"
                    aria-label="Back to top"
                >
                    <Monogram className="h-10 w-10 transition-transform duration-500 group-hover:scale-110" />
                    <span className="hidden font-cinzel text-[11px] font-semibold uppercase tracking-[0.35em] text-gold/90 sm:block">
                        Sarthak · Tanishka
                    </span>
                </button>

                <div className="hidden items-center gap-8 md:flex">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={l.testid}
                            onClick={() => go(l.id)}
                            className="group relative font-cinzel text-[11px] font-semibold uppercase tracking-[0.3em] text-sand transition-colors duration-300 hover:text-gold"
                        >
                            {l.label}
                            <span className="absolute -bottom-1.5 left-1/2 h-px w-0 -translate-x-1/2 bg-gold transition-all duration-500 group-hover:w-full" />
                        </button>
                    ))}
                </div>

                <button
                    data-testid="nav-menu-button"
                    onClick={() => setOpen((v) => !v)}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold md:hidden"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="border-b border-gold/15 bg-obsidian/95 backdrop-blur-xl md:hidden"
                    >
                        <div className="flex flex-col gap-1 px-6 py-4">
                            {LINKS.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`${l.testid}-mobile`}
                                    onClick={() => go(l.id)}
                                    className="py-3 text-left font-cinzel text-xs font-semibold uppercase tracking-[0.3em] text-sand transition-colors hover:text-gold"
                                >
                                    {l.label}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
